import { writable, derived, get } from 'svelte/store'
import {
  DEFAULT_MODE,
  LIVE_SECTION_ENABLED,
  LIVE_ORDERS_UI_ENABLED,
  normalizeExecution,
  normalizeMode,
  bankrollFromHealth,
} from './mode.js'
import {
  readHorizonFilter,
  writeHorizonFilter,
  normalizeHorizonFilter,
  glanceFromPaper,
  bothHorizons,
  accountGlance,
} from './lib/horizon.js'
import { forcedTradingMode } from './lib/hostSite.js'

/** @type {import('svelte/store').Writable<'paper'|'live'>} */
export const tradingMode = writable(forcedTradingMode() || DEFAULT_MODE)

/** Live dashboard section (status) — not order placement. */
export const liveSectionEnabled = writable(LIVE_SECTION_ENABLED)
/** Live order UI graduation — stays false. */
export const liveUiEnabled = writable(LIVE_ORDERS_UI_ENABLED)

/** Normalized execution block from API (mode, confirm, kill-switch, preflight). */
export const executionInfo = writable(normalizeExecution(null))

export const offlineMode = writable(false)

export const authUser = writable(null)

export const paperData = writable(null)
export const opsStatus = writable(null)
export const digestData = writable(null)
export const auditData = writable(null)
export const journalData = writable(null)

/** Consecutive poll failures for paper or ops status. */
export const pollFailStreak = writable(0)

export const bets = writable([])
export const betsHasMore = writable(false)
export const betsLoading = writable(false)

/** Paper bets trading day (MSK YYYY-MM-DD). Empty → API defaults to today. */
export const paperDay = writable('')
/** Paper calendar month map from last /api/paper (days + today). */
export const paperCalendar = writable(null)

/** Live bets trading day (MSK) — separate from paper. */
export const liveDay = writable('')
export const liveCalendar = writable(null)
export const liveBetsDay = writable(null)

export const opsInFlight = writable(false)

/** Bets / digest horizon filter: 15m | 5m | all */
export const horizonFilter = writable(readHorizonFilter())

/** Paper feed asset filter: btc | eth | eth-exit | all */
export const assetFilter = writable('all')

/** Allowed bets-page asset filter ids (classic coins + ETH dogon twin). */
export const PAPER_ASSET_FILTER_IDS = [
  'all',
  'btc',
  'eth',
  'eth-exit',
]

export function setHorizonFilter(next) {
  const hz = normalizeHorizonFilter(next)
  horizonFilter.set(hz)
  writeHorizonFilter(hz)
}

export function setAssetFilter(next) {
  const a = String(next || 'all').trim().toLowerCase()
  assetFilter.set(PAPER_ASSET_FILTER_IDS.includes(a) ? a : 'all')
}

/**
 * Apply `execution` from /api/paper or /api/ops/status (config-driven; no UI switch).
 * Accepts either the execution object or a parent payload that contains `.execution`.
 * @param {Record<string, unknown> | null | undefined} raw
 */
export function applyExecution(raw) {
  const block =
    raw && typeof raw === 'object' && raw.execution && typeof raw.execution === 'object'
      ? raw.execution
      : raw
  const ex = normalizeExecution(block)
  executionInfo.set(ex)
  const forced = forcedTradingMode()
  tradingMode.set(forced || normalizeMode(ex.execution_mode))
  if (ex.live_section_ready) liveSectionEnabled.set(true)
  if (ex.live_ui_ready) liveUiEnabled.set(true)
}

/** @deprecated alias — same store as executionInfo */
export const execution = executionInfo

export function notePollResult(ok) {
  if (ok) {
    pollFailStreak.set(0)
    offlineMode.set(false)
    return
  }
  const n = get(pollFailStreak) + 1
  pollFailStreak.set(n)
  if (n >= 2) offlineMode.set(true)
}

export const bankroll = derived([paperData, tradingMode], ([$paper, $mode]) => {
  if (!$paper || !$paper.health) return null
  return bankrollFromHealth($paper.health, $mode)
})

/** Dual-horizon glance (15m + 5m) from /api/paper. */
export const dualGlance = derived(paperData, ($paper) => glanceFromPaper($paper))

/** Flat per-horizon balances for shell title / Overview fallbacks. */
export const dualAccounts = derived(paperData, ($paper) => {
  const both = bothHorizons($paper)
  const glance = glanceFromPaper($paper)
  const fromPack = (hz) => {
    const pack = both[hz]
    const fromHealth = accountGlance(pack && pack.health)
    if (fromHealth.balance != null && Number.isFinite(Number(fromHealth.balance))) {
      return fromHealth
    }
    // Fall back to glance.bankroll when health lags but glance/cards have balance.
    const bit = glance && glance[hz]
    const bank = bit && bit.bankroll && typeof bit.bankroll === 'object' ? bit.bankroll : null
    if (bank && bank.balance != null && Number.isFinite(Number(bank.balance))) {
      return {
        ...fromHealth,
        balance: Number(bank.balance),
        today_pnl:
          bank.today_pnl != null && Number.isFinite(Number(bank.today_pnl))
            ? Number(bank.today_pnl)
            : fromHealth.today_pnl,
        open_stake:
          bank.open_stake != null && Number.isFinite(Number(bank.open_stake))
            ? Number(bank.open_stake)
            : fromHealth.open_stake,
        pending:
          bank.pending != null && Number.isFinite(Number(bank.pending))
            ? Number(bank.pending)
            : fromHealth.pending,
      }
    }
    return fromHealth
  }
  return {
    '15m': fromPack('15m'),
    '5m': fromPack('5m'),
  }
})

export const todayFee = derived([paperData, tradingMode], ([$paper, $mode]) => {
  if (normalizeMode($mode) !== 'live' || !$paper || !$paper.health) return null
  const h = $paper.health
  const candidates = [h.today_fee, h.live_fee_sum, h.paper_fee_est_sum]
  for (const v of candidates) {
    if (v != null && v !== '' && Number.isFinite(Number(v))) return Number(v)
  }
  return null
})
